import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    DefaultButton,
    PrimaryButton,
    Stack,
    Label,
    Shimmer,
    ShimmerElementType,
    Toggle,
} from "@fluentui/react";
import { querySessionMessages } from "src/services/aigc";
import { TimelineView } from "src/components/TimelineView";
import style from "./index.module.scss";

export const getQueryParam = (key: string) => new URLSearchParams(window.location.search).get(key) || "";

const JSON_PREVIEW_LINES = 100;

const JsonFileDisplay: React.FC<{ url: string; filename: string; onDataLoaded?: (data: any) => void }> = ({ url, filename, onDataLoaded }) => {
    const [json, setJson] = useState<string | null>(null);
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchJson = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const text = await response.text();
            // Try to pretty print
            try {
                const obj = JSON.parse(text);
                setJson(JSON.stringify(obj, null, 2));
                // Notify parent component with parsed data
                if (onDataLoaded) {
                    onDataLoaded(obj);
                }
            } catch {
                setJson(text); // fallback to raw
            }
        } catch (e: any) {
            if (e.name === "TypeError" && e.message.includes("fetch")) {
                setError("CORS error - unable to fetch file directly");
            } else {
                setError(e.message);
            }
        } finally {
            setLoading(false);
        }
    }, [url, onDataLoaded]);

    useEffect(() => {
        fetchJson();
    }, [fetchJson]);

    if (loading) {
        return (
            <div style={{ padding: "16px 0" }}>
                <Shimmer
                    shimmerElements={[
                        { type: ShimmerElementType.line, height: 16, width: "40%" },
                        { type: ShimmerElementType.gap, height: 8 },
                        { type: ShimmerElementType.line, height: 200, width: "100%" },
                    ]}
                />
            </div>
        );
    }
    if (error) {
        return (
            <div style={{ color: "red" }}>
                {`Error loading ${filename}: ${error}`}
            </div>
        );
    }
    if (!json) {
        return null;
    }

    const lines = json.split("\n");
    const isLong = lines.length > JSON_PREVIEW_LINES;
    const displayContent = expanded ? json : lines.slice(0, JSON_PREVIEW_LINES).join("\n");

    return (
        <div style={{
            background: "#f5f5f5",
            border: "1px solid #ccc",
            padding: 8,
            margin: "8px 0",
            fontFamily: "monospace",
            fontSize: 12,
            borderRadius: 4,
        }}
        >
            <div style={{
                marginBottom: 8,
                fontWeight: "bold",
                color: "#333",
            }}
            >
                {filename}
            </div>
            <div style={{
                maxHeight: 400,
                overflow: "auto",
                background: "white",
                padding: 8,
                border: "1px solid #ddd",
                borderRadius: 2,
            }}
            >
                <pre style={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                }}
                >
                    {displayContent}
                </pre>
            </div>
            {isLong && (
                <div style={{ marginTop: 8 }}>
                    <button
                        type="button"
                        onClick={() => setExpanded(!expanded)}
                        style={{
                            padding: "4px 8px",
                            fontSize: 12,
                            background: "#007acc",
                            color: "white",
                            border: "none",
                            borderRadius: 2,
                            cursor: "pointer",
                        }}
                    >
                        {expanded ? "Show less" : `Show more (${lines.length - JSON_PREVIEW_LINES} more lines)`}
                    </button>
                </div>
            )}
        </div>
    );
};

export const SessionDetail: React.FC = () => {
    const history = useNavigate();
    const sessionId = getQueryParam("sessionId");
    const [messagesUrl, setMessagesUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [timelineData, setTimelineData] = useState<any>(null);
    const [viewMode, setViewMode] = useState<"timeline" | "json">("timeline");

    const handleReplay = () => {
        if (!sessionId) {
            console.error("Session ID is missing");
            return;
        }
        const clippyBaseUrl = process.env.REACT_APP_CLIPPY_URL;
        if (!clippyBaseUrl) {
            console.error("REACT_APP_CLIPPY_URL is not configured");
            return;
        }
        const replayUrl = `${clippyBaseUrl}/${sessionId}/`;
        window.open(replayUrl, "_blank");
    };

    // Memoize the data loaded callback to prevent infinite re-renders
    const handleDataLoaded = useCallback((data: any) => {
        setTimelineData(data);
    }, []);

    useEffect(() => {
        if (!sessionId) return;

        setLoading(true);
        setError(null);
        querySessionMessages({ sessionId })
            .then((res) => {
                console.log("Session messages response:", res);
                // 假设 API 返回的是 JSON URL 字符串
                if (typeof res === "string") {
                    setMessagesUrl(res);
                } else if (res?.url) {
                    setMessagesUrl(res.url);
                } else {
                    setError("Invalid response format");
                }
            })
            .catch((err) => {
                console.error("Failed to fetch session messages:", err);
                setError(err.message || "Failed to fetch session messages");
            })
            .finally(() => {
                setLoading(false);
            });
    }, [sessionId]);

    return (
        <div className={style.sessionDetail}>
            <div className={style.header}>
                <div className={style.backButton}>
                    <DefaultButton
                        text="Back"
                        iconProps={{ iconName: "Back" }}
                        onClick={() => history("/workflow")}
                    />
                    <PrimaryButton
                        text="Replay"
                        iconProps={{ iconName: "Play" }}
                        onClick={handleReplay}
                    />
                </div>
                <h3 className={style.title}>Session Detail</h3>
            </div>
            <hr />
            <div>
                <Stack tokens={{ childrenGap: 50 }}>
                    <Stack tokens={{ childrenGap: 16, padding: "16px 12px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Label>Session Messages</Label>
                            {timelineData && (
                                <Toggle
                                    label="View Mode"
                                    inlineLabel
                                    onText="Timeline"
                                    offText="JSON"
                                    checked={viewMode === "timeline"}
                                    onChange={(_, checked) => setViewMode(checked ? "timeline" : "json")}
                                />
                            )}
                        </div>
                        {loading && (
                            <Shimmer
                                shimmerElements={[
                                    { type: ShimmerElementType.line, height: 16, width: "30%" },
                                    { type: ShimmerElementType.gap, height: 8 },
                                    { type: ShimmerElementType.line, height: 300, width: "100%" },
                                ]}
                            />
                        )}
                        {error && (
                            <div style={{ color: "red" }}>
                                Error:
                                {" "}
                                {error}
                            </div>
                        )}
                        {messagesUrl && !loading && !error && (
                            viewMode === "timeline" ? (
                                timelineData ? (
                                    <TimelineView data={timelineData} />
                                ) : (
                                    <div style={{ display: "none" }}>
                                        <JsonFileDisplay
                                            url={messagesUrl}
                                            filename="session-messages.json"
                                            onDataLoaded={handleDataLoaded}
                                        />
                                    </div>
                                )
                            ) : (
                                <JsonFileDisplay
                                    url={messagesUrl}
                                    filename="session-messages.json"
                                    onDataLoaded={handleDataLoaded}
                                />
                            )
                        )}
                    </Stack>
                </Stack>
            </div>
        </div>
    );
};
